import { useTheme } from "next-themes"
import React, { useState, useEffect, useRef } from "react"

interface IProps {
    label: string
    options?: any[] // Ensure options is an array
    onChange?: (selectedOption: { label: string; value: string }) => void
    isDisabled?: boolean
    defaultInputValue?: string
    name?: string
    value?: { label: string; value: string } | null
    required?: boolean
    placeholder?: string
    defaultKey?: string
}

const CustomSelect = ({
    label,
    onChange = (_selectedOption: { label: string; value: string }) => undefined,
    options,
    isDisabled,
    defaultInputValue,
    name,
    value,
    required,
    placeholder,
}: IProps) => {
    const { theme } = useTheme()
    const [mounted, setMounted] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [dynamicOptions, setDynamicOptions] = useState(options || [])
    const selectRef = useRef<HTMLSelectElement>(null)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        // Update dynamicOptions if the main options prop changes
        // If a search is active, re-apply it, otherwise just set options
        if (searchTerm) {
            const lowercasedFilter = searchTerm.toLowerCase()
            const filtered = (options || []).filter((option: any) =>
                option.label.toLowerCase().includes(lowercasedFilter)
            )
            setDynamicOptions(filtered)
        } else {
            setDynamicOptions(options || [])
        }
    }, [options]) // Only re-run if the options prop itself changes instance

    useEffect(() => {
        if (!searchTerm) {
            setDynamicOptions(options || [])
            return
        }
        const lowercasedFilter = searchTerm.toLowerCase()
        const filtered = (options || []).filter((option: any) =>
            option.label.toLowerCase().includes(lowercasedFilter)
        )
        setDynamicOptions(filtered)
    }, [searchTerm, options]) // Re-filter when searchTerm changes or options list changes

    const handleKeyDown = (event: React.KeyboardEvent<HTMLSelectElement>) => {
        if (event.key === "Backspace") {
            setSearchTerm((prev) => prev.slice(0, -1))
            event.preventDefault()
        } else if (event.key === "Escape") {
            setSearchTerm("")
            event.preventDefault()
        } else if (
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey
        ) {
            // Append character if it's a single, printable character (simplistic check)
            setSearchTerm((prev) => prev + event.key)
            event.preventDefault() // Prevent native select jump-to-option
        }
        // Do not prevent default for Tab, Enter, ArrowUp, ArrowDown to allow native select navigation/selection
    }

    const handleNativeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const selectedValue = event.target.value
        const selectedOption = (options || []).find(
            (option: any) => option.value === selectedValue
        )
        if (onChange && selectedOption) {
            onChange(selectedOption)
        } else if (onChange) {
            onChange({ value: selectedValue, label: selectedValue })
        }
        setSearchTerm("") // Reset search term on selection
    }

    const handleBlur = () => {
        // Reset search term when the select loses focus, after a short delay
        // This delay helps if the user clicks on a scrollbar within the select,
        // or if there's any other interaction that might briefly blur then refocus.
        setTimeout(() => {
            if (
                selectRef.current &&
                document.activeElement !== selectRef.current
            ) {
                setSearchTerm("")
            }
        }, 150)
    }

    const selectStyle: React.CSSProperties = {
        backgroundColor: !mounted
            ? "transparent"
            : theme === "dark"
              ? "#1d1644"
              : "#fff",
        color: !mounted ? "#fff" : theme === "dark" ? "#fff" : "initial",
        border: !mounted
            ? "1px solid #F0F0F080"
            : theme === "dark"
              ? "1px solid #7B91EF"
              : "1px solid #ccc", // Enhanced border on focus for dark
        borderRadius: "0.375rem",
        padding: "0.5rem",
        width: "100%",
        height: "42px", // Adjusted height slightly
    }

    const currentSelectValue = value?.value || defaultInputValue || ""

    return (
        <div>
            <label
                htmlFor={name || "select"}
                className="mb-1 block pb-1 text-sm capitalize text-gray-600 transition-all dark:text-gray-200 md:text-base"
            >
                {label}
                {required && <span className="ml-1 text-red-500">*</span>}{" "}
            </label>
            <select
                ref={selectRef}
                id={name || "select"}
                name={name}
                value={currentSelectValue}
                onChange={handleNativeChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                disabled={isDisabled}
                required={required}
                style={selectStyle}
                className="block w-full text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                size={
                    searchTerm && (dynamicOptions || []).length > 1
                        ? Math.min((dynamicOptions || []).length + 1, 5)
                        : 1
                } // Expand select on search
            >
                {/* Placeholder: Show if specified, no search term, and current value is empty (or matches placeholder value if it's explicitly set) */}
                {placeholder && !searchTerm && !currentSelectValue && (
                    <option value="" disabled hidden>
                        {placeholder}
                    </option>
                )}

                {(dynamicOptions || []).map(
                    (
                        option: { value: string; label: string },
                        index: number
                    ) => (
                        <option
                            key={option.value || index}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    )
                )}

                {/* "No matches" message: Show if search term exists and no dynamic options resulted */}
                {searchTerm && (dynamicOptions || []).length === 0 && (
                    <option value="" disabled>
                        No matches found
                    </option>
                )}
            </select>
        </div>
    )
}

export default CustomSelect
